import { useEffect, useState, useCallback } from 'react';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Lottie from 'lottie-react';
import ChatLoader from '../../assets/json/chatLoader.json'
import { Message } from '../../interfaces/Message';
import './chat.css';
import { useTranslation } from 'react-i18next';
import { TextField, useMediaQuery, useTheme, IconButton } from '@mui/material';
import Sidebar from '../../components/specific/chat/Sidebar';
import SendToMobileOutlinedIcon from '@mui/icons-material/SendToMobileOutlined';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from '../../enums/Approutes';
import ReactMarkdown from 'react-markdown';
import { convertHtmlToMarkdown, markdownComponents } from './molecules/markdown';
import SpendCreditModal from '../../components/specific/modal/SpendCreditModal';
import { useUserData } from '../../hooks/useUserData';
import { useToast } from '../../hooks/useToast';
import { chatService, billingService, userService } from '../../services';
import { useGlobalData } from '../../hooks/globalDataContext';
import { notifyCreditsUpdated } from '../../utils/creditsEvents';

const Chat = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const { userId } = useUserData();
    const { success, genericError } = useToast();
    const { refreshCredits } = useGlobalData();
    const [messages, setMessages] = useState<Message[]>([]);
    const [tokenError, setTokenError] = useState<boolean>(false);
    const [input, setInput] = useState<string>('');
    const [isThinking, setIsThinking] = useState<boolean>(false);
    const [conversations, setConversations] = useState<any[]>([]);
    const [selectedChatId, setSelectedChatId] = useState<number | null>(null);
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const [sidebarOpen, setSidebarOpen] = useState<boolean>(isDesktop);
    const [credits, setCredits] = useState<number>(0);
    const [showSpendCreditModal, setShowSpendCreditModal] = useState<boolean>(false);
    const [pendingChatId, setPendingChatId] = useState<number | null>(null);

    const mapChatMessage = (m: any): Message => {
        // Gestisce sia i messaggi con 'sender' che con 'role'
        let sender: 'user' | 'ai';

        if (m.sender) {
            sender = m.sender;
        } else if (m.role) {
            sender = m.role === 'user' ? 'user' : 'ai';
        } else if (m.sender_type) {
            sender = m.sender_type.toLowerCase() === 'user' ? 'user' : 'ai';
        } else {
            // Fallback: se non riusciamo a determinare, assumiamo che sia AI
            sender = 'ai';
        }

        return {
            sender,
            content: m.content,
        };
    };

    const setSidebarOpenCallback = useCallback((open: boolean) => {
        setSidebarOpen(open);
    }, []);

    useEffect(() => {
        setSidebarOpen(isDesktop);
    }, [isDesktop]);

    useEffect(() => {
        const fetchConversations = async () => {
            try {
                const data = await chatService.getConversations();
                const conversationsToSet = data.conversations || [];
                setConversations(conversationsToSet);
            } catch (err) {
                console.error('❌ Errore caricamento conversazioni:', err);
                genericError('caricamento conversazioni');
                setConversations([]);
            }
        };

        const fetchCredits = async () => {
            try {
                const data = await userService.getCredits();
                setCredits(data.credits);
            } catch (error) {
                console.error('Errore durante il recupero dei crediti:', error);
                genericError('recupero crediti');
            }
        };

        if (userId) {
            fetchConversations();
            fetchCredits();
        }
    }, [userId, genericError]);

    useEffect(() => {
        const container = document.getElementById('chat-scroll-container');
        if (container) {
            setTimeout(() => {
                container.scrollTop = container.scrollHeight;
            }, 0);
        }
    }, [messages]);

    const loadConversation = useCallback(async (chatId: number) => {
        try {
            const data = await chatService.getConversation(chatId);
            const adapted: Message[] = data.messages.map(mapChatMessage);
            setMessages(adapted);
            setSelectedChatId(chatId);

            // Se c'è una richiesta in pending per questa chat, ferma il loader
            if (pendingChatId === chatId) {
                setIsThinking(false);
                setPendingChatId(null);
            }

            if (!isDesktop) setSidebarOpenCallback(false);
        } catch (e) {
            console.error('Errore caricamento conversazione:', e);
            genericError('caricamento conversazione');
        }
    }, [isDesktop, genericError, setSidebarOpenCallback, pendingChatId]);

    const handleNewChat = useCallback(() => {
        setMessages([]);
        setSelectedChatId(null);

        // Se c'è una richiesta in pending, ferma il loader
        if (isThinking) {
            setIsThinking(false);
            setPendingChatId(null);
        }

        if (!isDesktop) setSidebarOpenCallback(false);
    }, [isDesktop, setSidebarOpenCallback, isThinking]);

    const handleSpendCreditConfirm = useCallback(async () => {
        try {
            await billingService.spendCreditsForChat();
            setShowSpendCreditModal(false);
            setCredits(credits);
            // Aggiorna i crediti e notifica altri componenti
            refreshCredits(true);
            notifyCreditsUpdated();
            success(t('toast:ai_chat_success'));
        } catch (error) {
            console.error('Errore durante la spesa del credito:', error);
            genericError('spesa credito AI');
        }
    }, [credits, success, genericError, refreshCredits, t]);

    // Gestione modifica chat
    const handleEditChat = useCallback(async (chatId: number, newTitle: string) => {
        try {
            await chatService.editChat(chatId, { title: newTitle });

            // Aggiorna la lista delle conversazioni
            const updatedConversations = conversations.map(conv =>
                conv.id === chatId ? { ...conv, name: newTitle } : conv
            );
            setConversations(updatedConversations);

            // Se la chat modificata è quella selezionata, aggiorna anche l'ID
            if (selectedChatId === chatId) {
                setSelectedChatId(chatId);
            }

            success('Chat modificata con successo');
        } catch (error) {
            console.error('Errore durante la modifica della chat:', error);
            genericError('modifica chat');
        }
    }, [conversations, selectedChatId, success, genericError]);

    // Gestione cancellazione chat
    const handleDeleteChat = useCallback(async (chatId: number) => {
        try {
            await chatService.deleteChat(chatId);

            // Rimuovi la chat dalla lista
            const updatedConversations = conversations.filter(conv => conv.id !== chatId);
            setConversations(updatedConversations);

            // Se la chat cancellata era selezionata, resetta la selezione
            if (selectedChatId === chatId) {
                setMessages([]);
                setSelectedChatId(null);
            }

            success('Chat eliminata con successo');
        } catch (error) {
            console.error('Errore durante la cancellazione della chat:', error);
            genericError('cancellazione chat');
        }
    }, [conversations, selectedChatId, success, genericError]);

    const handleSend = useCallback(async () => {
        const userMessage: Message = { sender: 'user', content: input };
        setMessages(prev => [...prev, userMessage]);
        const content = input;
        setInput('');
        setIsThinking(true);

        // Salva l'ID della chat originale per verificare se è cambiata durante la richiesta
        const originalChatId = selectedChatId;
        setPendingChatId(originalChatId);

        try {
            const response = await chatService.sendMessage(content, originalChatId);
            const data = response.content;

            // Verifica se la chat è ancora la stessa prima di aggiungere la risposta
            if (selectedChatId === originalChatId) {
                setMessages(prev => [...prev, { sender: 'ai', content: data }]);

                // Se è una nuova chat (selectedChatId === null), crea la conversazione nella sidebar
                if (originalChatId === null) {
                    const newConversation = {
                        id: response.conversation,
                        name: content.length > 30 ? content.substring(0, 30) + '...' : content,
                        created_at: new Date().toISOString()
                    };

                    // Aggiungi la nuova conversazione alla lista
                    setConversations(prev => [newConversation, ...prev]);

                    // Imposta la nuova chat come selezionata
                    setSelectedChatId(response.conversation);
                }
            } else {
                // Se la chat è cambiata, non aggiungere la risposta qui
                // La risposta verrà caricata quando si carica la chat originale
                console.log('Chat cambiata durante la richiesta, risposta non aggiunta');
            }

            setTokenError(false);
        } catch (error: any) {
            console.error('Errore durante la richiesta:', error);
            if (error.response?.status === 403) {
                if (credits > 0) {
                    setShowSpendCreditModal(true);
                } else {
                    setTokenError(true);
                }
            } else {
                genericError('invio messaggio AI');
            }
        } finally {
            setIsThinking(false);
            setPendingChatId(null);
        }
    }, [input, selectedChatId, credits, genericError]);

    const TokenErrorBanner = () => {
        if (!tokenError) return null;

        return (
            <div className='fixed left-[-1rem] w-full bottom-[6rem] md:left-[15.4rem] md:h-[3rem] flex flex-col md:flex-row items-center justify-center md:justify-start z-index-1000 p-4 mx-4 gap-1 md:gap-2'>
                <div className='font-regular text-xs md:text-sm'>
                    <strong className='text-primary'>{t('billing:token_error_title')}</strong> {t('billing:token_error_subtitle')}
                </div>
                <div className='w-full md:w-auto'>
                    <button
                        className='w-full bg-primary text-white font-regular text-xs md:text-sm rounded px-2 py-1 hover:bg-primary-hover'
                        onClick={() => navigate(AppRoutes.BILLING)}
                    >
                        {t('billing:token_error_button')}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col flex-1 h-[93vh] min-h-0 realtive">
            {!isDesktop && (
                <div className="w-full relative flex items-center justify-center h-14 p-4 pb-2 md:hidden">
                    <IconButton
                        onClick={() => setSidebarOpenCallback(true)}
                        aria-label="open sidebar"
                        className="absolute left-1"
                    >
                        <SendToMobileOutlinedIcon />
                    </IconButton>
                    <span className="text-base font-semibold">{t('billing:pinnalo_ai_chat')}</span>
                </div>
            )}
            <Sidebar
                open={sidebarOpen}
                onClose={() => setSidebarOpenCallback(false)}
                conversations={conversations}
                selectedId={selectedChatId}
                onSelect={loadConversation}
                onNewChat={handleNewChat}
                onEditChat={handleEditChat}
                onDeleteChat={handleDeleteChat}
            />
            {/* Area dei messaggi */}
            <div id="chat-scroll-container" className="flex-grow md:ml-[240px] p-4 md:p-8 hide-scrollbar w-auto overflow-y-auto overflow-x-hidden pb-[96px] md:pb-[120px]">
                {messages.map((msg, idx) => (
                    <div
                        key={idx}
                        style={{
                            display: 'flex',
                            justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                            margin: '1rem',
                            height: 'auto'
                        }}
                    >
                        <div
                            style={{
                                maxWidth: '80%',
                                padding: '0.75rem 1.6rem',
                                borderRadius: '1rem',
                                background: msg.sender === 'user' ? '#E9495A' : '#EAEAEA',
                                color: msg.sender === 'user' ? 'white' : 'black',
                                fontWeight: 'semibold',
                                wordWrap: 'break-word',
                                whiteSpace: 'pre-wrap',
                                overflowWrap: 'anywhere'
                            }}
                        >
                            {msg.sender === 'user' ? (
                                msg.content
                            ) : (
                                <ReactMarkdown components={markdownComponents}>
                                    {convertHtmlToMarkdown(msg.content)}
                                </ReactMarkdown>
                            )}
                        </div>
                    </div>
                ))}
                {isThinking && selectedChatId === pendingChatId && (
                    <div className='flex justify-start mb-2'>
                        <div
                            style={{
                                padding: '0.75rem 1rem',
                                borderRadius: '1rem',
                                background: '#EAEAEA',
                                fontStyle: 'italic',
                            }}
                        >
                            <Lottie animationData={ChatLoader} style={{ width: '12rem', height: '6rem' }} />
                        </div>
                    </div>
                )}
            </div>

            {/* Area input */}
            <div className="fixed bottom-0 left-0 right-0 z-90 md:ml-[240px] px-4 py-2 md:px-10 bg-white flex flex-col">
                <div className="flex items-center w-full">
                    <TextField
                        type="text"
                        value={input}
                        placeholder={t('billing:placeholder')}
                        onChange={(e) => setInput(e.target.value)}
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        disabled={tokenError}
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: '#E0E0E0',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#E0E0E0',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#E0E0E0',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: '#000000',
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                                color: '#000000',
                            },
                            '& .MuiInputBase-input.Mui-disabled': {
                                color: '#000000',
                            },
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
                    />
                    <button
                        onClick={handleSend}
                        className="ml-3 p-2 rounded-full bg-primary text-white hover:bg-primary-hover"
                    >
                        <ArrowUpwardIcon />
                    </button>
                </div>
                <div className='w-full text-center'>
                    <p className='text-xs' style={{ color: '#ADADAD' }}>{t('billing:warningText')}</p>
                </div>
            </div>
            <TokenErrorBanner />

            <SpendCreditModal
                open={showSpendCreditModal}
                onClose={() => {
                    setShowSpendCreditModal(false);
                }}
                onConfirm={handleSpendCreditConfirm}
            />
        </div>
    );
};

export default Chat;
